import dayjs from 'dayjs';
import {
  EARLY_LEAVE_DAYS, STAGES, SOURCE, addDays, branchById, candById, contractState, currentContract, daysBetween, deptOf, empById, empContracts,
  fixedCount, fmtD, headcountAt, healthState, inScope, insState, isStaffAt, isWorking, licenseState, medicalLeadOf, missingDocs, noticeShortfall,
  offTasksFor, pct, planStatus, positionById, reqById, reqOpenSlots, settleDeadline, stageAt, toDay, LICENSE_KIND, OFF_TASKS,
  type Candidate, type Employee, type HrState, type Scope, type Source, type Stage,
} from './hrLogic';

/* ============================== Việc cần xử lý ============================== */

export type ActGroup = 'contract' | 'probation' | 'insurance' | 'license' | 'docs' | 'offboarding' | 'recruit' | 'branch';
export const ACT_GROUP: Record<ActGroup, string> = {
  contract: 'Hợp đồng', probation: 'Thử việc', insurance: 'Bảo hiểm', license: 'Giấy phép hành nghề', docs: 'Hồ sơ & sức khỏe',
  offboarding: 'Nghỉ việc', recruit: 'Tuyển dụng', branch: 'Chi nhánh',
};
export type ActLevel = 'crit' | 'warn' | 'info';
export interface ActionItem {
  key: string; level: ActLevel; group: ActGroup; title: string; detail: string;
  empId?: string; reqId?: string; candId?: string; offId?: string; contractId?: string; branchId?: string; deptId?: string;
  /** Hạn xử lý để sắp xếp */
  due?: string;
}

const LEVEL_ORDER: Record<ActLevel, number> = { crit: 0, warn: 1, info: 2 };

export function actionItems(s: HrState, today: string, sc: Scope = {}): ActionItem[] {
  const out: ActionItem[] = [];
  const push = (a: ActionItem) => out.push(a);
  const pos = (e: Employee) => positionById(s, e.positionId)?.name || '';
  const br = (id: string) => branchById(s, id)?.short || '';

  // Cơ sở khám chữa bệnh phải có người phụ trách chuyên môn có giấy phép còn hạn
  s.branches.filter(b => b.clinic && (!sc.branchId || b.id === sc.branchId)).forEach(b => {
    if (!medicalLeadOf(s, b.id, today)) {
      push({ key: `lead-${b.id}`, level: 'crit', group: 'branch', branchId: b.id, title: `${b.name} chưa có người phụ trách chuyên môn`, detail: 'Cơ sở thẩm mỹ phải có bác sĩ phụ trách chuyên môn có giấy phép hành nghề còn hạn — chỉ định người hoặc tuyển gấp' });
    }
  });

  let healthDue = 0;
  s.employees.filter(e => inScope(s, e, sc)).forEach(e => {
    const who = `${e.name} · ${pos(e)} · ${br(e.branchId)}`;
    if (e.hireDate <= today && isWorking(e, today)) {
      const c = currentContract(s, e.id, today);
      if (!c) push({ key: `noc-${e.id}`, level: 'crit', group: 'contract', empId: e.id, title: 'Đang làm việc nhưng chưa có hợp đồng', detail: who, due: e.hireDate });
      else {
        if (!c.signedAt && !e.leftDate) push({ key: `uns-${c.id}`, level: 'crit', group: 'contract', empId: e.id, contractId: c.id, title: `${c.kind === 'probation' ? 'Thỏa thuận thử việc' : 'Hợp đồng'} ${c.code} chưa ký`, detail: `${who} · hiệu lực từ ${fmtD(c.start)}`, due: c.start });
        const st = contractState(s, c, today);
        const left = c.end ? daysBetween(today, c.end) : 0;
        if (st === 'expired' && c.kind === 'probation') push({ key: `cst-${c.id}`, level: 'crit', group: 'contract', empId: e.id, contractId: c.id, title: 'Đạt thử việc nhưng chưa ký HĐ chính thức', detail: `${who} · hết thử việc ${fmtD(c.end)}`, due: c.end });
        else if (st === 'expired') push({ key: `cst-${c.id}`, level: 'crit', group: 'contract', empId: e.id, contractId: c.id, title: `HĐ đã hết hạn ${-left} ngày, chưa gia hạn`, detail: `${who} · ${c.code} hết hạn ${fmtD(c.end)}`, due: c.end });
        else if (st === 'auto_indefinite') push({ key: `cst-${c.id}`, level: 'crit', group: 'contract', empId: e.id, contractId: c.id, title: `HĐ quá hạn ${-left} ngày vẫn làm việc`, detail: `${who} · quá 30 ngày nên HĐ đã trở thành không xác định thời hạn — ký HĐ không XĐTH`, due: c.end });
        else if (st === 'expiring') push({
          key: `cst-${c.id}`, level: left <= 10 ? 'crit' : 'warn', group: 'contract', empId: e.id, contractId: c.id,
          title: `HĐ hết hạn sau ${left} ngày`,
          detail: `${who} · ${fmtD(c.end)}${fixedCount(s, e.id) >= 2 ? ' · đã ký 2 lần XĐTH, lần này phải ký không XĐTH' : ''}`, due: c.end,
        });
        else if (st === 'eval_due') push({ key: `cst-${c.id}`, level: 'warn', group: 'probation', empId: e.id, contractId: c.id, title: `Hết thử việc sau ${left} ngày — cần đánh giá`, detail: `${who} · hết hạn ${fmtD(c.end)}`, due: c.end });
        else if (st === 'eval_overdue') push({ key: `cst-${c.id}`, level: 'crit', group: 'probation', empId: e.id, contractId: c.id, title: `Quá hạn thử việc ${-left} ngày chưa có kết quả`, detail: `${who} · hết thử việc ${fmtD(c.end)} — thông báo kết quả và ký HĐ hoặc chấm dứt`, due: c.end });
      }
      const ins = insState(s, e, today);
      if (ins === 'missing' || ins === 'late') push({ key: `ins-${e.id}`, level: ins === 'late' ? 'crit' : 'warn', group: 'insurance', empId: e.id, title: ins === 'late' ? 'Trễ hạn báo tăng BHXH' : 'Chưa báo tăng BHXH', detail: `${who} · HĐLĐ từ ${fmtD(c?.start)}` });
      const lic = licenseState(s, e, today);
      if (lic.state === 'missing') push({ key: `lic-${e.id}`, level: lic.need === 'gphn' ? 'crit' : 'warn', group: 'license', empId: e.id, title: `Chưa có ${LICENSE_KIND[lic.need!].toLowerCase()}`, detail: `${who} — không được thực hiện thủ thuật khi chưa có giấy phép` });
      else if (lic.state === 'expired') push({ key: `lic-${e.id}`, level: 'crit', group: 'license', empId: e.id, title: `${LICENSE_KIND[lic.need!]} đã hết hạn`, detail: `${who} · số ${lic.lic?.number} hết hạn ${fmtD(lic.lic?.expiresAt)}`, due: lic.lic?.expiresAt });
      else if (lic.state === 'expiring') push({ key: `lic-${e.id}`, level: 'warn', group: 'license', empId: e.id, title: `${LICENSE_KIND[lic.need!]} hết hạn sau ${lic.days} ngày`, detail: `${who} · số ${lic.lic?.number}`, due: lic.lic?.expiresAt });
      const miss = missingDocs(s, e);
      if (miss.length && daysBetween(e.hireDate, today) <= 60) push({ key: `doc-${e.id}`, level: 'warn', group: 'docs', empId: e.id, title: `Thiếu ${miss.length} giấy tờ hồ sơ nhận việc`, detail: `${who}` });
      if (['overdue', 'missing'].includes(healthState(e, today).state) && daysBetween(e.hireDate, today) > 30) healthDue += 1;
    }
    // Người sắp nhận việc: thỏa thuận thử việc phải ký trước ngày vào làm
    if (e.hireDate > today && daysBetween(today, e.hireDate) <= 7) {
      const c = empContracts(s, e.id)[0];
      if (!c || !c.signedAt) push({ key: `up-${e.id}`, level: 'warn', group: 'contract', empId: e.id, contractId: c?.id, title: `Nhận việc ${fmtD(e.hireDate)}: ${c ? 'thỏa thuận thử việc chưa ký' : 'chưa lập thỏa thuận thử việc'}`, detail: who, due: e.hireDate });
    }
    const ins = insState(s, e, today);
    if (ins === 'decrease_pending') push({ key: `insd-${e.id}`, level: 'crit', group: 'insurance', empId: e.id, title: `Đã nghỉ ${daysBetween(e.leftDate!, today)} ngày, chưa báo giảm BHXH`, detail: `${who} — công ty vẫn phải đóng BHXH cho tới khi báo giảm`, due: e.leftDate });
  });
  if (healthDue) push({ key: 'health', level: 'info', group: 'docs', title: `${healthDue} nhân sự quá hạn khám sức khỏe định kỳ`, detail: 'Khám sức khỏe định kỳ tối thiểu mỗi năm một lần' });

  s.offboardings.filter(o => o.status === 'open').forEach(o => {
    const e = empById(s, o.empId);
    if (!e || !inScope(s, e, sc)) return;
    const who = `${e.name} · ${pos(e)} · ${br(e.branchId)}`;
    const tasks = offTasksFor(s, o);
    const done = tasks.filter(t => o.tasks[t]).length;
    const deadline = settleDeadline(o);
    if (!o.tasks.settlement && deadline < today) push({ key: `off-set-${o.id}`, level: 'crit', group: 'offboarding', empId: e.id, offId: o.id, title: `Quá hạn thanh toán nghỉ việc ${daysBetween(deadline, today)} ngày`, detail: `${who} · nghỉ ${fmtD(o.lastDay)}, hạn thanh toán ${fmtD(deadline)}`, due: deadline });
    else if (o.lastDay < today) push({ key: `off-${o.id}`, level: 'warn', group: 'offboarding', empId: e.id, offId: o.id, title: `Chưa xong thủ tục nghỉ việc (${done}/${tasks.length})`, detail: `${who} · nghỉ ${fmtD(o.lastDay)} · còn: ${tasks.filter(t => !o.tasks[t]).map(t => OFF_TASKS[t].split(',')[0].toLowerCase()).join(', ')}`, due: deadline });
    else {
      const short = noticeShortfall(s, o);
      push({ key: `off-${o.id}`, level: short ? 'warn' : 'info', group: 'offboarding', empId: e.id, offId: o.id, title: `Nghỉ việc ngày ${fmtD(o.lastDay)} (còn ${daysBetween(today, o.lastDay)} ngày)`, detail: `${who}${short ? ` · báo trước ${short.given} ngày, chưa đủ ${short.days} ngày theo HĐ` : ` · đã xong ${done}/${tasks.length} việc bàn giao`}`, due: o.lastDay });
    }
  });

  // Thiếu so với định biên mà chưa có yêu cầu tuyển (gộp theo chi nhánh × bộ phận)
  s.branches.filter(b => !sc.branchId || b.id === sc.branchId).forEach(b => s.depts.forEach(d => {
    const st = planStatus(s, b.id, d.id, today);
    if (st.uncovered <= 0) return;
    const leavingNames = s.employees.filter(e => e.branchId === b.id && deptOf(s, e) === d.id && e.leftDate && e.leftDate >= addDays(today, -60) && e.leaveType !== 'probation_fail')
      .map(e => `${e.name} (${e.leftDate! < today ? 'nghỉ' : 'sẽ nghỉ'} ${fmtD(e.leftDate)})`);
    push({
      key: `gap-${b.id}-${d.id}`, level: 'info', group: 'recruit', branchId: b.id, deptId: d.id,
      title: `${d.name} · ${b.short} thiếu ${st.uncovered} người, chưa có yêu cầu tuyển`,
      detail: `Định biên ${st.plan}, hiện ${st.actual - st.leaving}${st.recruiting ? `, đang tuyển ${st.recruiting}` : ''}${leavingNames.length ? ` · ${leavingNames.join(', ')}` : ''}`,
    });
  }));

  s.requisitions.filter(r => !sc.branchId || r.branchId === sc.branchId).forEach(r => {
    const p = positionById(s, r.positionId)?.name;
    if (r.status === 'pending') push({ key: `req-${r.id}`, level: r.overPlan ? 'warn' : 'info', group: 'recruit', reqId: r.id, title: `Yêu cầu tuyển ${r.code} chờ duyệt`, detail: `${r.qty} ${p} · ${br(r.branchId)}${r.overPlan ? ' · vượt định biên' : ''}`, due: r.deadline });
    if (r.status === 'approved' && reqOpenSlots(s, r) > 0 && r.deadline < today) push({ key: `reqd-${r.id}`, level: 'warn', group: 'recruit', reqId: r.id, title: `Quá hạn tuyển ${daysBetween(r.deadline, today)} ngày`, detail: `${r.code} · ${p} · ${br(r.branchId)} · còn thiếu ${reqOpenSlots(s, r)}`, due: r.deadline });
  });

  let stale = 0;
  s.candidates.forEach(c => {
    const r = reqById(s, c.requisitionId);
    if (!r || (sc.branchId && r.branchId !== sc.branchId) || c.outcome !== 'open') return;
    const p = positionById(s, r.positionId)?.name;
    if (c.offer?.status === 'need_approval') push({ key: `offa-${c.id}`, level: 'warn', group: 'recruit', candId: c.id, reqId: r.id, title: 'Offer ngoài khung lương chờ duyệt', detail: `${c.name} · ${p} · ${br(r.branchId)}` });
    if (c.offer?.status === 'accepted') push({ key: `offh-${c.id}`, level: c.offer.startDate <= today ? 'warn' : 'info', group: 'recruit', candId: c.id, reqId: r.id, title: `Nhận việc ngày ${fmtD(c.offer.startDate)}`, detail: `${c.name} · ${p} · ${br(r.branchId)} — chuẩn bị hồ sơ, thỏa thuận thử việc`, due: c.offer.startDate });
    const last = c.history[c.history.length - 1]?.at || c.appliedAt;
    if ((c.stage === 'applied' || c.stage === 'screening') && daysBetween(toDay(last), today) > 3) stale += 1;
  });
  if (stale) push({ key: 'stale', level: 'info', group: 'recruit', title: `${stale} hồ sơ ứng viên chờ xử lý quá 3 ngày`, detail: 'Ứng viên tốt thường nhận việc nơi khác sau 1 tuần' });

  return out.sort((a, b) => LEVEL_ORDER[a.level] - LEVEL_ORDER[b.level] || (a.due || '9999').localeCompare(b.due || '9999'));
}

/* ============================== Biến động nhân sự ============================== */

/** Người nghỉ trong khoảng [from, to] (theo ngày làm việc cuối) */
export const leaversIn = (s: HrState, from: string, to: string, sc: Scope = {}) =>
  s.employees.filter(e => inScope(s, e, sc) && e.leftDate && e.leftDate >= from && e.leftDate <= to && isStaffAt(s, e, e.leftDate));
export const hiresIn = (s: HrState, from: string, to: string, sc: Scope = {}) =>
  s.employees.filter(e => inScope(s, e, sc) && e.hireDate >= from && e.hireDate <= to && isStaffAt(s, e, e.hireDate));

export function movement(s: HrState, from: string, to: string, sc: Scope = {}) {
  const hires = hiresIn(s, from, to, sc);
  const leavers = leaversIn(s, from, to, sc);
  const start = headcountAt(s, from, sc);
  const end = headcountAt(s, to, sc);
  const avg = (start + end) / 2;
  const voluntary = leavers.filter(e => e.leaveType === 'voluntary').length;
  return { hires: hires.length, leavers: leavers.length, voluntary, start, end, turnover: pct(leavers.length, avg), voluntaryRate: pct(voluntary, avg) };
}

/** Tỷ lệ nghỉ việc 12 tháng = số nghỉ 12 tháng / quân số bình quân 12 tháng */
export function annualTurnover(s: HrState, today: string, sc: Scope = {}) {
  const from = dayjs(today).subtract(12, 'month').format('YYYY-MM-DD');
  const samples = Array.from({ length: 13 }, (_, i) => headcountAt(s, dayjs(from).add(i, 'month').format('YYYY-MM-DD'), sc));
  const avg = samples.reduce((a, b) => a + b, 0) / samples.length;
  const leavers = leaversIn(s, addDays(from, 1), today, sc);
  return { rate: pct(leavers.length, avg), leavers: leavers.length, avg: Math.round(avg) };
}

export function monthlySeries(s: HrState, today: string, months = 12, sc: Scope = {}) {
  return Array.from({ length: months }, (_, i) => {
    const m = dayjs(today).subtract(months - 1 - i, 'month');
    const from = m.startOf('month').format('YYYY-MM-DD');
    const end = m.endOf('month').format('YYYY-MM-DD');
    const to = end > today ? today : end;
    const leavers = leaversIn(s, from, to, sc);
    return {
      month: m.format('MM/YY'),
      hires: hiresIn(s, from, to, sc).length,
      voluntary: leavers.filter(e => e.leaveType === 'voluntary').length,
      other: leavers.filter(e => e.leaveType !== 'voluntary').length,
      headcount: headcountAt(s, to, sc),
    };
  });
}

/** Nghỉ sớm: trong số người vào làm 12 tháng qua (đã đủ 90 ngày), bao nhiêu người nghỉ trong 90 ngày đầu */
export function earlyAttrition(s: HrState, today: string, sc: Scope = {}) {
  const from = dayjs(today).subtract(12, 'month').format('YYYY-MM-DD');
  const cohort = hiresIn(s, from, addDays(today, -EARLY_LEAVE_DAYS), sc);
  const early = cohort.filter(e => e.leftDate && daysBetween(e.hireDate, e.leftDate) < EARLY_LEAVE_DAYS);
  return { cohort: cohort.length, early: early.length, rate: pct(early.length, cohort.length), people: early };
}

export function probationStats(s: HrState, today: string, sc: Scope = {}) {
  const from = dayjs(today).subtract(12, 'month').valueOf();
  const evals = s.contracts.filter(c => c.kind === 'probation' && c.evaluation && c.evaluation.at >= from)
    .filter(c => { const e = empById(s, c.empId); return !!e && inScope(s, e, sc); });
  const pass = evals.filter(c => c.evaluation!.result === 'pass').length;
  const inProbation = s.employees.filter(e => inScope(s, e, sc) && isWorking(e, today) && e.hireDate <= today && currentContract(s, e.id, today)?.kind === 'probation' && !e.leftDate);
  return { pass, fail: evals.length - pass, rate: pct(pass, evals.length), inProbation };
}

export const TENURE_BUCKETS = ['Dưới 3 tháng', '3–12 tháng', '1–2 năm', '2–5 năm', 'Trên 5 năm'];
export function tenureBucket(from: string, to: string) {
  const m = dayjs(to).diff(dayjs(from), 'month', true);
  return m < 3 ? 0 : m < 12 ? 1 : m < 24 ? 2 : m < 60 ? 3 : 4;
}

/** Cơ cấu nhân sự hiện tại */
export function composition(s: HrState, today: string, sc: Scope = {}) {
  const staff = s.employees.filter(e => inScope(s, e, sc) && isStaffAt(s, e, today));
  const ctv = s.employees.filter(e => inScope(s, e, sc) && isWorking(e, today) && e.hireDate <= today && !isStaffAt(s, e, today)).length;
  const byDept = s.depts.map(d => ({ dept: d, n: staff.filter(e => deptOf(s, e) === d.id).length })).filter(x => x.n);
  const kinds = { probation: 0, fixed: 0, indefinite: 0, none: 0 };
  staff.forEach(e => {
    const c = currentContract(s, e.id, today);
    if (!c) kinds.none += 1; else if (c.kind !== 'service') kinds[c.kind] += 1;
  });
  const tenure = TENURE_BUCKETS.map(() => 0);
  staff.forEach(e => { tenure[tenureBucket(e.hireDate, today)] += 1; });
  const female = staff.filter(e => e.gender === 'Nữ').length;
  return { total: staff.length, ctv, byDept, kinds, tenure, female, male: staff.length - female };
}

/** Phân tích nghỉ việc 12 tháng: lý do, loại, thâm niên khi nghỉ */
export function leaverAnalysis(s: HrState, today: string, sc: Scope = {}) {
  const from = dayjs(today).subtract(12, 'month').format('YYYY-MM-DD');
  const list = leaversIn(s, addDays(from, 1), today, sc);
  const reasons = new Map<string, number>();
  list.forEach(e => reasons.set(e.leaveReason || 'Không ghi lý do', (reasons.get(e.leaveReason || 'Không ghi lý do') || 0) + 1));
  const tenure = TENURE_BUCKETS.map(() => 0);
  list.forEach(e => { tenure[tenureBucket(e.hireDate, e.leftDate!)] += 1; });
  const byDept = s.depts.map(d => ({ dept: d, n: list.filter(e => deptOf(s, e) === d.id).length })).filter(x => x.n).sort((a, b) => b.n - a.n);
  return { total: list.length, reasons: Array.from(reasons, ([reason, n]) => ({ reason, n })).sort((a, b) => b.n - a.n), tenure, byDept };
}

/* ============================== Chi nhánh ============================== */

export type Risk = 'high' | 'mid' | 'low';
export function branchHealth(s: HrState, today: string) {
  const acts = actionItems(s, today);
  const q90 = addDays(today, -90);
  return s.branches.map(b => {
    const sc = { branchId: b.id };
    const headcount = headcountAt(s, today, sc);
    const plan = s.depts.reduce((a, d) => a + (s.plan[`${b.id}|${d.id}`] || 0), 0);
    const mv = movement(s, q90, today, sc);
    const turnover = annualTurnover(s, today, sc);
    const mine = acts.filter(a => (a.branchId || empById(s, a.empId)?.branchId || reqById(s, a.reqId)?.branchId) === b.id);
    const crit = mine.filter(a => a.level === 'crit').length;
    const warn = mine.filter(a => a.level === 'warn').length;
    const lead = b.clinic ? medicalLeadOf(s, b.id, today) : undefined;
    const fill = pct(headcount, plan);
    const open = s.requisitions.filter(r => r.branchId === b.id).reduce((a, r) => a + (r.status === 'approved' ? reqOpenSlots(s, r) : 0), 0);
    const reasons: string[] = [];
    if (b.clinic && !lead) reasons.push('Thiếu người phụ trách chuyên môn');
    if (crit) reasons.push(`${crit} việc khẩn cấp`);
    if (turnover.rate > 30) reasons.push(`Nghỉ việc 12 tháng ${turnover.rate}%`);
    if (plan && fill < 85) reasons.push(`Mới đủ ${fill}% định biên`);
    const risk: Risk = (b.clinic && !lead) || crit >= 4 || turnover.rate > 45 ? 'high' : crit || warn >= 3 || turnover.rate > 30 || (plan && fill < 85) ? 'mid' : 'low';
    return { branch: b, headcount, plan, fill, hires: mv.hires, leavers: mv.leavers, turnover: turnover.rate, crit, warn, lead, open, risk, reasons };
  });
}

/* ============================== Tuyển dụng ============================== */

const inRange = (t: number | undefined, from: number, to: number) => !!t && t >= from && t <= to;
const candScope = (s: HrState, c: Candidate, sc: Scope) => {
  const r = reqById(s, c.requisitionId);
  return !!r && (!sc.branchId || r.branchId === sc.branchId) && (!sc.deptId || positionById(s, r.positionId)?.deptId === sc.deptId);
};

export function recruitMetrics(s: HrState, from: number, to: number, sc: Scope = {}) {
  const cands = s.candidates.filter(c => candScope(s, c, sc));
  const applied = cands.filter(c => inRange(c.appliedAt, from, to));
  const funnel = STAGES.map(st => ({ stage: st as Stage, n: applied.filter(c => !!stageAt(c, st)).length }));
  const hired = cands.filter(c => c.outcome === 'hired' && inRange(stageAt(c, 'hired'), from, to));
  const fillDays = hired.map(c => {
    const r = reqById(s, c.requisitionId);
    return r?.approvedAt ? daysBetween(toDay(r.approvedAt), toDay(stageAt(c, 'hired')!)) : null;
  }).filter((x): x is number => x !== null);
  const hireDays = hired.map(c => daysBetween(toDay(c.appliedAt), toDay(stageAt(c, 'hired')!)));
  const offers = cands.filter(c => c.offer && inRange(c.offer.resolvedAt, from, to) && (c.offer.status === 'accepted' || c.offer.status === 'declined' || c.outcome === 'hired'));
  const accepted = offers.filter(c => c.offer!.status === 'accepted' || c.outcome === 'hired').length;
  const closed = cands.filter(c => inRange(c.closedAt, from, to));
  const count = (list: Candidate[]) => {
    const m = new Map<string, number>();
    list.forEach(c => m.set(c.closeReason || 'Khác', (m.get(c.closeReason || 'Khác') || 0) + 1));
    return Array.from(m, ([reason, n]) => ({ reason, n })).sort((a, b) => b.n - a.n);
  };
  const avg = (xs: number[]) => (xs.length ? Math.round(xs.reduce((a, b) => a + b, 0) / xs.length) : null);
  return {
    applied: applied.length, funnel, hired: hired.length,
    timeToFill: avg(fillDays), timeToHire: avg(hireDays),
    offers: offers.length, offerRate: offers.length ? pct(accepted, offers.length) : null,
    rejectReasons: count(closed.filter(c => c.outcome === 'rejected')),
    withdrawReasons: count(closed.filter(c => c.outcome === 'withdrawn')),
  };
}

/** Hiệu quả nguồn tuyển: không chỉ số hồ sơ mà cả chất lượng (qua thử việc, ở lại sau 90 ngày) */
export function sourceStats(s: HrState, today: string, sc: Scope = {}) {
  const from = dayjs(today).subtract(12, 'month').valueOf();
  const cands = s.candidates.filter(c => candScope(s, c, sc) && c.appliedAt >= from);
  return (Object.keys(SOURCE) as Source[]).map(src => {
    const list = cands.filter(c => c.source === src);
    const hired = list.filter(c => c.outcome === 'hired');
    const emps = hired.map(c => empById(s, c.empId)).filter((e): e is Employee => !!e);
    const probation = emps.map(e => empContracts(s, e.id).find(c => c.kind === 'probation')?.evaluation).filter(Boolean);
    const passed = probation.filter(ev => ev!.result === 'pass').length;
    const early = emps.filter(e => e.leftDate && daysBetween(e.hireDate, e.leftDate) < EARLY_LEAVE_DAYS).length;
    return {
      source: src, applied: list.length, interviewed: list.filter(c => !!stageAt(c, 'interview')).length, hired: hired.length,
      hireRate: pct(hired.length, list.length), passed, evaluated: probation.length, early,
    };
  }).filter(r => r.applied).sort((a, b) => b.hired - a.hired || b.applied - a.applied);
}

/** Thời gian trung bình ứng viên nằm ở mỗi bước (ngày) — tìm nút thắt quy trình */
export function stageDurations(s: HrState, from: number, to: number, sc: Scope = {}) {
  const cands = s.candidates.filter(c => candScope(s, c, sc) && inRange(c.appliedAt, from, to));
  return STAGES.slice(0, -1).map((st, i) => {
    const xs = cands.map(c => {
      const a = stageAt(c, st);
      const b = stageAt(c, STAGES[i + 1]);
      return a && b ? (b - a) / 86400000 : null;
    }).filter((x): x is number => x !== null);
    return { stage: st, days: xs.length ? Math.round((xs.reduce((a, b) => a + b, 0) / xs.length) * 10) / 10 : null };
  });
}

export const candidateName = (s: HrState, id?: string) => candById(s, id)?.name || '';
